import { useMemo, useState, useEffect } from "react";
import { Plus, Building2, User, FileText, CheckSquare, Square, Download, ArrowDownLeft, ArrowUpRight, Pencil, ArrowLeftRight, Trash2, GitMerge, ArchiveRestore, ShieldCheck } from "lucide-react";
import { Page, Panel, DeleteButton, SearchInput, ExportMenu, FileSpreadsheet, BalancePill, usePagination, Pagination, useSort, SortTh, TotalsBar } from "../lib/ui";
import { can, money, download, api } from "../lib/api";
import { DuplicatesModal, MergeModal } from "./CustomerMerge";
import { BalanceCheckModal } from "./BalanceCheck";

export default function Customers({ rows, setModal, onDelete, user, onOpenStatement, onQuickPayment, onReload }) {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [cariFilter, setCariFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [archivedRows, setArchivedRows] = useState(null);
  const isArchive = cariFilter === "archived";
  const source = useMemo(() => (isArchive ? archivedRows || [] : rows), [isArchive, archivedRows, rows]);

  useEffect(() => {
    setSelected((sel) => new Set([...sel].filter((id) => source.some((r) => r.id === id))));
  }, [source]);

  const filtered = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    return source.filter((c) => {
      if (cariFilter === "cari" && c.is_cari === false) return false;
      if (cariFilter === "noncari" && c.is_cari !== false) return false;
      if (tab === "debtor" && !(c.balance > 0.01)) return false;
      if (tab === "creditor" && !(c.balance < -0.01)) return false;
      if (tab === "recent" && !(c.last_activity_at && c.last_activity_at >= cutoff)) return false;
      if (!q) return true;
      return (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.tax_no || c.tc_no || "").includes(q);
    });
  }, [source, q, tab, cariFilter]);

  const counts = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const base = source.filter((c) => cariFilter === "all" || isArchive || (cariFilter === "cari" ? c.is_cari !== false : c.is_cari === false));
    return {
      all: base.length,
      debtor: base.filter((c) => c.balance > 0.01).length,
      creditor: base.filter((c) => c.balance < -0.01).length,
      recent: base.filter((c) => c.last_activity_at && c.last_activity_at >= cutoff).length,
    };
  }, [source, cariFilter, isArchive]);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allChecked = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allChecked) filtered.forEach((r) => n.delete(r.id));
    else filtered.forEach((r) => n.add(r.id));
    return n;
  });
  const srt = useSort(filtered, {
    name: (c) => c.name, type: (c) => c.type, id: (c) => c.tax_no || c.tc_no || "", office: (c) => c.tax_office || "",
    city: (c) => `${c.city || ""} ${c.district || ""}`, balance: (c) => Number(c.balance || 0),
  });
  const pg = usePagination(srt.sorted);
  const bal = useMemo(() => {
    const sum = (f) => filtered.reduce((t, c) => t + (Number(f(c)) || 0), 0);
    return { resmi: sum((c) => c.resmi_balance ?? c.balance), proforma: sum((c) => c.proforma_balance), all: sum((c) => c.balance) };
  }, [filtered]);
  const balItem = (label, v, testid, grand) => ({
    label, value: money(Math.abs(v)), testid, hint: v > 0.01 ? "Borç" : v < -0.01 ? "Alacak" : "",
    tone: `${v > 0.01 ? "negative" : v < -0.01 ? "positive" : ""} ${grand ? "grand" : ""}`,
  });

  const [bulkMsg, setBulkMsg] = useState("");
  const [dups, setDups] = useState([]);
  const [showDups, setShowDups] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [merging, setMerging] = useState(null);
  const loadDups = () => api.get("/customers/duplicates").then((r) => setDups(r.data)).catch(() => {});
  useEffect(() => { loadDups(); }, [rows]);
  useEffect(() => {
    if (cariFilter === "archived") api.get("/customers?archived=true").then((r) => setArchivedRows(r.data)).catch(() => setArchivedRows([]));
  }, [cariFilter, rows]);
  const byId = useMemo(() => Object.fromEntries(rows.map((c) => [c.id, c])), [rows]);
  const findReason = (x, y) => dups.find((p) => (p.a === x && p.b === y) || (p.a === y && p.b === x))?.reason || "";
  const afterChange = (m) => { setBulkMsg(m); setSelected(new Set()); onReload?.(); };
  const deleteOne = async (c) => {
    try {
      const { data: u } = await api.get(`/customers/${c.id}/usage`);
      const text = u.used
        ? `"${c.name}" carisinin hareketleri var (${u.summary}).\n\nBu cari SİLİNMEYECEK, ARŞİVLENECEK. Ekstresi ve hareketleri korunur; "Arşivdekiler" filtresinden geri alabilirsiniz.\n\nOnaylıyor musunuz?`
        : `"${c.name}" carisinin hiç hareketi yok, kalıcı olarak silinecek. Emin misiniz?`;
      if (!window.confirm(text)) return;
      const { data } = await api.delete(`/customers/${c.id}`);
      afterChange(data.archived ? `${c.name} arşivlendi.` : `${c.name} silindi.`);
    } catch (e) { setBulkMsg(e.response?.data?.detail || "Silinemedi"); }
  };
  const unarchive = async (c) => {
    try { await api.post(`/customers/${c.id}/unarchive`); afterChange(`${c.name} arşivden çıkarıldı.`); }
    catch (e) { setBulkMsg(e.response?.data?.detail || "İşlem yapılamadı"); }
  };
  const bulkCari = async (is_cari) => {
    try {
      const { data } = await api.post("/customers/bulk-cari", { ids: [...selected], is_cari });
      setBulkMsg(`${data.updated} cari "${is_cari ? "Cari kaydı olanlar" : "Cari kaydı olmayanlar"}" listesine aktarıldı.`);
      setSelected(new Set()); onReload?.();
    } catch (e) { setBulkMsg(e.response?.data?.detail || "Aktarılamadı"); }
  };
  const bulkDelete = async () => {
    try {
      const ids = [...selected];
      const { data: u } = await api.post("/customers/bulk-usage", { ids });
      const free = ids.length - u.used.length;
      const text = `${free} cari kalıcı olarak silinecek.` + (u.used.length
        ? `\n\nHareketi olduğu için ${u.used.length} cari SİLİNMEYECEK, ARŞİVLENECEK:\n` + u.used.slice(0, 15).map((x) => `• ${x.name} (${x.summary})`).join("\n") + (u.used.length > 15 ? `\n… ve ${u.used.length - 15} cari daha` : "")
        : "") + "\n\nOnaylıyor musunuz?";
      if (!window.confirm(text)) return;
      const { data } = await api.post("/customers/bulk-delete", { ids, archive_used: true });
      afterChange(`${data.deleted} cari silindi` + (data.archived ? `, ${data.archived} cari arşivlendi.` : "."));
    } catch (e) { setBulkMsg(e.response?.data?.detail || "Silinemedi"); }
  };

  const bulkExport = async (fmt) => {
    const res = await api.post("/customers/export/bulk", { ids: [...selected], fmt }, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `cariler-secim.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 400);
  };

  return (
    <>
      <Page
        eyebrow="MÜŞTERİ & TEDARİKÇİ"
        title="Cari Hesaplar"
        subtitle="Şahıs ve kurumsal cari kartlarınızı tek yerden yönetin."
        action={
          <div className="button-row">
            {user?.role === "admin" && (
              <button data-testid="open-balance-check-button" className="secondary-button" onClick={() => setShowCheck(true)}>
                <ShieldCheck size={15} /> Bakiye kontrolü
              </button>
            )}
            {can(user, "edit_customer") && can(user, "delete_customer") && (
              <button data-testid="open-duplicates-button" className={`secondary-button ${dups.length ? "tone-warning" : ""}`} onClick={() => setShowDups(true)}>
                <GitMerge size={15} /> Olası tekrarlar {dups.length > 0 && <b data-testid="duplicates-count">({dups.length})</b>}
              </button>
            )}
            <ExportMenu testid="export-customers" options={[
              { key: "xlsx", label: "Excel (.xlsx)", icon: <FileSpreadsheet size={14} />, onClick: () => download("/customers/export?fmt=xlsx", "cariler.xlsx") },
              { key: "csv", label: "CSV (.csv)", icon: <FileSpreadsheet size={14} />, onClick: () => download("/customers/export?fmt=csv", "cariler.csv") },
            ]} />
            <button data-testid="add-customers-button" className="primary-button" disabled={!can(user, "create_customer")}
                    title={!can(user, "create_customer") ? "Bu işlem için yetkiniz yok" : ""}
                    onClick={() => setModal({ type: "customer" })}>
              <Plus size={17} /> Yeni cari
            </button>
          </div>
        }
      />

      {selected.size > 0 && (
        <div className="bulk-bar" data-testid="bulk-customers-toolbar">
          <span><strong data-testid="bulk-customers-count">{selected.size}</strong> cari seçildi</span>
          <div className="bulk-actions">
            <button data-testid="bulk-cust-export-xlsx" className="secondary-button" onClick={() => bulkExport("xlsx")}>
              <Download size={14} /> Excel
            </button>
            <button data-testid="bulk-cust-export-csv" className="secondary-button" onClick={() => bulkExport("csv")}>
              <Download size={14} /> CSV
            </button>
            {can(user, "edit_customer") && <>
              <button data-testid="bulk-cust-to-cari" className="secondary-button" onClick={() => bulkCari(true)}>
                <ArrowLeftRight size={14} /> Cari kaydı olanlara aktar
              </button>
              <button data-testid="bulk-cust-to-noncari" className="secondary-button" onClick={() => bulkCari(false)}>
                <ArrowLeftRight size={14} /> Cari kaydı olmayanlara aktar
              </button>
            </>}
            {can(user, "edit_customer") && can(user, "delete_customer") && selected.size === 2 && !isArchive && (() => {
              const [x, y] = [...selected];
              return byId[x] && byId[y] && (
                <button data-testid="bulk-cust-merge" className="secondary-button" onClick={() => setMerging({ a: byId[x], b: byId[y], reason: findReason(x, y) })}>
                  <GitMerge size={14} /> Birleştir
                </button>
              );
            })()}
            {can(user, "delete_customer") && !isArchive && (
              <button data-testid="bulk-cust-delete" className="secondary-button tone-danger" onClick={bulkDelete}>
                <Trash2 size={14} /> Toplu sil
              </button>
            )}
            <button data-testid="bulk-cust-clear" className="text-button" onClick={() => setSelected(new Set())}>
              Seçimi temizle
            </button>
          </div>
        </div>
      )}
      {bulkMsg && <div className="sync-msg" data-testid="bulk-cust-msg" onClick={() => setBulkMsg("")}>{bulkMsg}</div>}
      {showCheck && <BalanceCheckModal onClose={() => setShowCheck(false)} onOpenStatement={(c) => { setShowCheck(false); onOpenStatement?.(c); }} />}
      {showDups && <DuplicatesModal pairs={dups} byId={byId} onClose={() => setShowDups(false)}
                                    onPick={(p) => { setShowDups(false); setMerging({ a: byId[p.a], b: byId[p.b], reason: p.reason }); }} />}
      {merging && <MergeModal {...merging} onClose={() => setMerging(null)}
                              onMerged={(m) => { setMerging(null); afterChange(m); }} />}

      <Panel
        className="module-panel"
        action={
          <div className="filter-row">
            <div className="tab-group">
              {[["all", "Tümü"], ["debtor", "Borçlu"], ["creditor", "Alacaklı"], ["recent", "Son 30 gün"]].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`customers-tab-${id}`}
                  className={tab === id ? "tab active" : "tab"}
                  onClick={() => setTab(id)}
                  type="button"
                >
                  {label} <b>{counts[id]}</b>
                </button>
              ))}
            </div>
            <select data-testid="customers-cari-filter" className="cari-filter-select" value={cariFilter} onChange={(e) => setCariFilter(e.target.value)}>
              <option value="all">Tüm kayıtlar</option>
              <option value="cari">{`Cari kaydı olanlar (${rows.filter((c) => c.is_cari !== false).length})`}</option>
              <option value="noncari">{`Cari kaydı olmayanlar (${rows.filter((c) => c.is_cari === false).length})`}</option>
              <option value="archived">{`Arşivdekiler${archivedRows ? ` (${archivedRows.length})` : ""}`}</option>
            </select>
            <SearchInput value={q} onChange={setQ} testid="customers-search" placeholder="İsim, TC, vergi no" />
          </div>
        }
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <button data-testid="select-all-customers" type="button" className="check-btn" onClick={toggleAll}>
                    {allChecked ? <CheckSquare size={15} /> : <Square size={15} />}
                  </button>
                </th>
                <SortTh label="Cari" k="name" s={srt} testid="customers-sort-name" /><SortTh label="Tip" k="type" s={srt} testid="customers-sort-type" />
                <SortTh label="Kimlik / Vergi" k="id" s={srt} testid="customers-sort-id" />
                <SortTh label="Vergi Dairesi" k="office" s={srt} testid="customers-sort-office" /><SortTh label="Şehir / İlçe" k="city" s={srt} testid="customers-sort-city" />
                <SortTh label="Bakiye" k="balance" s={srt} align="right" testid="customers-sort-balance" /><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="8" className="empty">Henüz cari kart eklenmedi.</td></tr>
              )}
              {pg.slice.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} className={selected.has(c.id) ? "row-selected" : ""}>
                  <td>
                    <button data-testid={`select-customer-${c.id}`} type="button" className="check-btn" onClick={() => toggleOne(c.id)}>
                      {selected.has(c.id) ? <CheckSquare size={15} /> : <Square size={15} />}
                    </button>
                  </td>
                  <td>
                    <strong>{c.name}</strong>
                    <small>{c.phone || c.email || "—"}</small>
                  </td>
                  <td>
                    <span className={`type-tag ${c.type}`}>
                      {c.type === "corporate" ? <Building2 size={12} /> : <User size={12} />}
                      {c.type === "corporate" ? "Kurumsal" : "Şahıs"}
                    </span>
                  </td>
                  <td>{c.type === "corporate" ? c.tax_no : c.tc_no || "—"}</td>
                  <td>{c.tax_office || "—"}</td>
                  <td>{[c.city, c.district].filter(Boolean).join(" / ") || "—"}</td>
                  <td className="amount">
                    <div className="balance-cell">
                      <strong data-testid={`customer-balance-${c.id}`}
                              className={c.balance > 0 ? "negative" : c.balance < 0 ? "positive" : ""}>
                        {money(Math.abs(c.balance || 0))}
                      </strong>
                      <BalancePill balance={c.balance || 0} />
                    </div>
                  </td>
                  <td className="amount actions-cell">
                    <button data-testid={`edit-customer-${c.id}`} className="row-icon tone-muted"
                            title={can(user, "edit_customer") ? "Cariyi düzenle" : "Bu işlem için yetkiniz yok"}
                            disabled={!can(user, "edit_customer")}
                            onClick={() => setModal({ type: "customer", initial: c })}>
                      <Pencil size={15} />
                    </button>
                    <button data-testid={`transfer-customer-${c.id}`} className="row-icon tone-info"
                            title={can(user, "create_transaction") ? "Bu cariden başka cariye aktarım yap" : "Bu işlem için yetkiniz yok"}
                            disabled={!can(user, "create_transaction")}
                            onClick={() => setModal({ type: "customer-transfer", fromCustomerId: c.id })}>
                      <ArrowLeftRight size={15} />
                    </button>
                    <button data-testid={`quick-tahsilat-${c.id}`} className="row-icon tone-positive"
                            title={can(user, "create_transaction") ? "Tahsilat ekle" : "Bu işlem için yetkiniz yok"}
                            disabled={!can(user, "create_transaction")}
                            onClick={() => onQuickPayment(c, "in")}>
                      <ArrowDownLeft size={15} />
                    </button>
                    <button data-testid={`quick-odeme-${c.id}`} className="row-icon tone-warning"
                            title={can(user, "create_transaction") ? "Ödeme yap" : "Bu işlem için yetkiniz yok"}
                            disabled={!can(user, "create_transaction")}
                            onClick={() => onQuickPayment(c, "out")}>
                      <ArrowUpRight size={15} />
                    </button>
                    <button data-testid={`statement-customer-${c.id}`} className="row-icon tone-muted"
                            title="Ekstre" onClick={() => onOpenStatement(c)}>
                      <FileText size={15} />
                    </button>
                    {isArchive ? (
                      <button data-testid={`unarchive-customer-${c.id}`} className="secondary-button" disabled={!can(user, "delete_customer")}
                              onClick={() => unarchive(c)}>
                        <ArchiveRestore size={14} /> Arşivden çıkar
                      </button>
                    ) : (
                      <DeleteButton
                        testid={`delete-customer-${c.id}`}
                        disabled={!can(user, "delete_customer")}
                        onClick={() => deleteOne(c)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TotalsBar testid="customers-totals" items={[
            { label: `${filtered.length} cari`, value: "", testid: "customers-totals-count" },
            balItem("Resmi bakiye", bal.resmi, "customers-totals-resmi"),
            balItem("Proforma bakiye", bal.proforma, "customers-totals-proforma"),
            balItem("Genel bakiye", bal.all, "customers-totals-grand", true),
          ]} />
          <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                      onChange={pg.setPage} testid="customers-pagination" />
        </div>
      </Panel>
    </>
  );
}
